import React from 'react';

import { Area, CartesianGrid, ComposedChart, Line, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { useSelector } from 'react-redux';

import T from '@src/assets/icons/T.svg';
import H from '@src/assets/icons/H.svg';
import D from '@src/assets/icons/D.svg';
import { ArcSkeleton, ArcStack, ArcText } from '@src/core/ui';
import { arcSpatium } from '@src/core/extensions';
import { useUnitType } from '@src/core/features/settings';

import { terrainCacheKey } from '@src/features/order/pages/upsert-mission-page/config/cache-keys';
import { useTerrainSeries } from '@src/features/order/pages/upsert-mission-page/hooks';
import { selectWaypoints, useGetTerrainAwareMutation } from '@src/features/order/pages/upsert-mission-page/redux';

import { TerrainAwareProps } from './types';


const safeColor = '#7B68EE';
const unsafeColor = '#FB5454';
const terrainFill = '#007200';
const crashZoneColor = '#FFC107';

const chartMargin = { top: 3, right: 20, left: 10, bottom: 30 };
const whiteAxis = { stroke: 'white' };
const yAxisTick = { fill: 'white', fontSize: 11, fontFamily: "'Roboto', sans-serif" };
const xDomain: [string, string] = ['dataMin', 'dataMax'];
const yAxisPadding = { top: 10, bottom: 0 };
const containerSx = { opacity: 0.8 };


const TerrainAware: React.FC<TerrainAwareProps> = () => {
  const wps = useSelector(selectWaypoints);
  const [getTerrainAware] = useGetTerrainAwareMutation({ fixedCacheKey: terrainCacheKey });

  const {
    isLoading,
    isAmsl,
    isAgl,
    droneData,
    terrainData,
    crashZoneData,
    wpDistances,
    unit,
    hasCrashZoneCollision,
  } = useTerrainSeries();
  useUnitType();

  const coordsKey = React.useMemo(() => {
    return JSON.stringify(wps.map((wp) => `${wp.latitude}-${wp.longitude}`));
  }, [wps]);

  React.useEffect(() => {
    if (wps.length < 1) return;
    // terrain-aware returns ground elevation by coordinates only — the backend reads
    // lat/lon and ignores altitude. Send just coordinates (no altitude conversion needed).
    getTerrainAware(wps.map((wp) => ({ latitude: wp.latitude, longitude: wp.longitude })));
  }, [coordsKey]);

  // Floor value for the chart: lowest altitude among terrain & drone points.
  // Used as Area baseValue so terrain fill always reaches the chart bottom
  // (works correctly when terrain dips below the launch reference into negative).
  const chartFloor = React.useMemo(() => {
    const values = [
      ...terrainData.map((p) => Number(p.altitude)),
      ...droneData.map((p) => Number(p.altitude)),
    ].filter((v) => Number.isFinite(v));
    if (values.length === 0) return 0;
    return Math.min(...values);
  }, [terrainData, droneData]);

  const yDomain = React.useMemo<[number, string]>(
    () => [chartFloor, 'auto'],
    [chartFloor],
  );

  const totalDistance = React.useMemo(() => {
    if (droneData.length === 0) return 0;
    return droneData[droneData.length - 1].distance;
  }, [droneData]);

  const xAxisTicks = React.useMemo(() => {
    if (isAgl) return wpDistances.map((d) => d.distance);
    return droneData.map((d) => d.distance);
  }, [droneData, isAgl, wpDistances]);

  const tickData = isAgl ? wpDistances : droneData;

  const axisTick = React.useMemo(
    () => <CustomizedAxisTick data={tickData} />,
    [tickData]
  );

  const droneStroke = hasCrashZoneCollision ? unsafeColor : safeColor;

  if (isLoading) return <ArcSkeleton width='100%' height='100%' />;

  return (
    <ArcStack
      p={1}
      width='100%'
      height='100%'
      bgcolor='black'
      borderRadius={1}
      spacing={1.5}
      sx={{ ...containerSx, minHeight: 0 }}>
      <ArcText.caption
        color='white'
        textAlign='center'
        fontFamily='Roboto'
        fontSize={12}
        sx={{ flexShrink: 0, pt: 0.5 }}
      >
        Total Distance: {arcSpatium(totalDistance)}
      </ArcText.caption>

      <ArcStack sx={{ flex: 1, minHeight: 0, width: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart margin={chartMargin}>
            <defs>
              <linearGradient id="terrainGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={terrainFill} stopOpacity={0.8} />
                <stop offset="100%" stopColor={terrainFill} stopOpacity={0.8} />
              </linearGradient>
              <linearGradient id="crashZoneGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={crashZoneColor} stopOpacity={0.5} />
                <stop offset="100%" stopColor={crashZoneColor} stopOpacity={0.1} />
              </linearGradient>
            </defs>

            <CartesianGrid
              stroke="white"
              opacity={0.2}
              vertical={true}
              horizontal={true}
            />

            <XAxis
              ticks={xAxisTicks}
              interval={0}
              type='number'
              dataKey='distance'
              domain={xDomain}
              axisLine={whiteAxis}
              tickLine={whiteAxis}
              tick={axisTick}
            />

            <YAxis
              domain={yDomain}
              padding={yAxisPadding}
              axisLine={whiteAxis}
              tickLine={whiteAxis}
              tick={yAxisTick}
              label={{
                value: isAmsl
                  ? `Height (AMSL) ${unit}`
                  : isAgl
                    ? `Height (Above Terrain) ${unit}`
                    : `Height (Relative to Launch) ${unit}`,
                angle: -90,
                position: 'insideLeft',
                fill: 'white',
                fontSize: 11,
                style: { textAnchor: 'middle', fontFamily: "'Roboto', sans-serif" },
              }}
            />

            <Area
              type="monotone"
              data={crashZoneData}
              dataKey="altitude"
              baseValue={chartFloor}
              stroke={crashZoneColor}
              strokeDasharray="5 5"
              strokeWidth={1}
              fill="url(#crashZoneGradient)"
              isAnimationActive={false}
              dot={false}
              activeDot={false}
            />

            <Area
              type="monotone"
              data={terrainData}
              dataKey="altitude"
              baseValue={chartFloor}
              stroke={terrainFill}
              strokeWidth={1}
              fill="url(#terrainGradient)"
              isAnimationActive={false}
              dot={false}
              activeDot={false}
            />

            <Line
              type="linear"
              data={droneData}
              dataKey="altitude"
              stroke={droneStroke}
              strokeWidth={2}
              dot={false}
              activeDot={false}
              isAnimationActive={false}
            />

          </ComposedChart>
        </ResponsiveContainer>
      </ArcStack>

      <ChartLegend />
    </ArcStack>

  );
};


const ChartLegend: React.FC = () => (
  <ArcStack direction="row" justifyContent="flex-start" gap={2} mt={0.5} pl={5}>
    <ArcStack direction="row" alignItems="center" gap={0.5}>

      <ArcStack
        width={28}
        height={6}
        sx={{
          border: '1px solid',
          borderColor: terrainFill,
          borderRadius: 3,
          bgcolor: `${terrainFill}20`,
        }}
      />

      <ArcText.caption color="white" fontSize={10}>Terrain</ArcText.caption>
    </ArcStack>

    <ArcStack direction="row" alignItems="center" gap={0.5}>
      <ArcStack
        width={28}
        height={6}
        sx={{
          border: '1px dashed',
          borderColor: crashZoneColor,
          borderRadius: 3,
          bgcolor: `${crashZoneColor}20`,
        }}
      />
      <ArcText.caption color="white" fontSize={10}>Crash zone</ArcText.caption>
    </ArcStack>

    <ArcStack direction="row" alignItems="center" gap={0.5}>
      <ArcStack
        width={28}
        height={6}
        sx={{ bgcolor: unsafeColor, borderRadius: 3 }}
      />
      <ArcText.caption color="white" fontSize={10}>Unsafe drone path</ArcText.caption>
    </ArcStack>

    <ArcStack direction="row" alignItems="center" gap={0.5}>
      <ArcStack
        width={28}
        height={6}
        sx={{ bgcolor: safeColor, borderRadius: 3 }}
      />
      <ArcText.caption color="white" fontSize={10}>Safe drone path</ArcText.caption>
    </ArcStack>
  </ArcStack>
);


const CustomizedAxisTick = (props: any) => {
  const { x, y, payload, data } = props;

  const index = payload.index;
  const current = data[index];

  if (index === 0) {
    return (
      <g transform={`translate(${x},${y})`}>
        <image href={T} x={-8} y={-25} width={16} height={16} />
        <TickText> {payload.value} </TickText>
      </g>
    );
  }

  if (index === data.length - 1) {
    return (
      <g transform={`translate(${x},${y})`}>
        <image href={H} x={-8} y={-25} width={16} height={16} />
        <TickText> {payload.value} </TickText>
      </g>
    );
  }

  if (current?.orderId) {
    return (
      <g transform={`translate(${x},${y})`}>
        <image href={D} x={-8} y={-25} width={16} height={16} />
        <TickText> {payload.value} </TickText>
      </g>
    );
  }

  return (
    <g transform={`translate(${x},${y})`}>
      <g transform="translate(0, -16)">
        <circle r={8} fill='#9664F8' />
        <text
          fill="white"
          fontSize={10}
          fontWeight="bold"
          textAnchor="middle"
          dominantBaseline="central"
        >
          {current.wpCount}
        </text>
      </g>

      <TickText> {payload.value} </TickText>
    </g>
  );
};


const TickText: React.FC<React.PropsWithChildren> = ({ children }) => (
  <text
    x={0}
    y={0}
    dy={4}
    fill="white"
    fontSize={11}
    fontFamily="'Roboto', sans-serif"
    textAnchor='end'
    transform="rotate(-90)"
  >
    {children}
  </text>
);


export default TerrainAware;
